import { api } from './api';
import { logger } from '../utils/logger';

export interface TokenInfo {
  token: string;
  expiresAt: number;
  userId: string;
}

export interface RefreshResponse {
  access_token: string;
  token_type: string;
}

export const TOKEN_INFO_EVENT = 'gotrutax-token-info';

class TokenService {
  private refreshPromise: Promise<string> | null = null;
  private autoRefreshTimer: NodeJS.Timeout | null = null;
  private readonly TOKEN_KEY = 'token';
  private readonly TOKEN_INFO_KEY = 'token_info';
  private readonly REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes before expiry
  
  // Refresh attempt tracking and circuit breaker
  private refreshAttempts: number = 0;
  private lastRefreshAttempt: number = 0;
  private refreshFailureCount: number = 0;
  private isRefreshBlocked: boolean = false;
  private refreshBlockedUntil: number = 0;
  private lastSuccessfulRefresh: number = 0;
  
  // Rate limiting constants
  private readonly MAX_REFRESH_ATTEMPTS_PER_MINUTE = 5;
  private readonly MAX_CONSECUTIVE_FAILURES = 3;
  private readonly MIN_REFRESH_INTERVAL = 2000; // 2 seconds minimum between attempts
  private readonly REFRESH_BLOCK_DURATION = 60000; // 1 minute block after too many failures
  private readonly REFRESH_ATTEMPT_WINDOW = 60000; // 1 minute window for attempt counting
  private readonly POST_REFRESH_GRACE_PERIOD = 15000; // 15 seconds grace period after successful refresh

  /**
   * Get the current token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Set the token in localStorage
   * @param token The new token to store
   * @param skipAutoRefresh Skip automatic refresh setup (used during refresh operations)
   */
  setToken(token: string, skipAutoRefresh: boolean = false): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    this.updateTokenInfo(token);
    this.lastSuccessfulRefresh = Date.now();
    
    // Only set up auto-refresh if not skipping (prevents recursive loops during refresh)
    if (!skipAutoRefresh) {
      this.setupAutoRefresh();
    }
  }

  /**
   * Remove the token from localStorage
   */
  removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_INFO_KEY);
    this.emitTokenInfoUpdated(null);
    this.clearAutoRefresh();
    // Reset refresh tracking
    this.resetRefreshTracking();
  }

  /**
   * Reset refresh attempt tracking
   */
  private resetRefreshTracking(): void {
    this.refreshAttempts = 0;
    this.lastRefreshAttempt = 0;
    this.refreshFailureCount = 0;
    this.isRefreshBlocked = false;
    this.refreshBlockedUntil = 0;
    this.lastSuccessfulRefresh = 0;
  }

  /**
   * Check if refresh is currently blocked due to too many failures
   */
  private isRefreshCurrentlyBlocked(): boolean {
    const now = Date.now();
    
    // Check if we're in a blocked period
    if (this.isRefreshBlocked && now < this.refreshBlockedUntil) {
      logger.log(`TokenService: Refresh blocked until ${new Date(this.refreshBlockedUntil).toISOString()}`);
      return true;
    }
    
    // Reset block if time has passed
    if (this.isRefreshBlocked && now >= this.refreshBlockedUntil) {
      logger.log('TokenService: Refresh block period expired, resetting');
      this.isRefreshBlocked = false;
      this.refreshFailureCount = 0;
    }
    
    return false;
  }

  private isWithinPostRefreshGracePeriod(): boolean {
    if (!this.lastSuccessfulRefresh) {
      return false;
    }

    const elapsed = Date.now() - this.lastSuccessfulRefresh;
    return elapsed < this.POST_REFRESH_GRACE_PERIOD;
  }

  private getPostRefreshDelay(): number {
    if (!this.lastSuccessfulRefresh) {
      return this.MIN_REFRESH_INTERVAL;
    }

    const elapsed = Date.now() - this.lastSuccessfulRefresh;
    const remaining = this.POST_REFRESH_GRACE_PERIOD - elapsed;
    if (remaining <= 0) {
      return this.MIN_REFRESH_INTERVAL;
    }

    return Math.max(this.MIN_REFRESH_INTERVAL, remaining);
  }

  /**
   * Check if we can attempt a refresh based on rate limiting
   */
  private canAttemptRefresh(): boolean {
    const now = Date.now();
    
    // Check if refresh is blocked
    if (this.isRefreshCurrentlyBlocked()) {
      return false;
    }
    
    // Check minimum interval between attempts
    if (this.lastRefreshAttempt > 0 && (now - this.lastRefreshAttempt) < this.MIN_REFRESH_INTERVAL) {
      logger.log(`TokenService: Too soon since last refresh attempt (${now - this.lastRefreshAttempt}ms ago)`);
      return false;
    }
    
    // Reset attempt counter if window has passed
    if (this.lastRefreshAttempt > 0 && (now - this.lastRefreshAttempt) > this.REFRESH_ATTEMPT_WINDOW) {
      logger.log('TokenService: Refresh attempt window expired, resetting counter');
      this.refreshAttempts = 0;
    }
    
    // Check rate limiting
    if (this.refreshAttempts >= this.MAX_REFRESH_ATTEMPTS_PER_MINUTE) {
      logger.log(`TokenService: Too many refresh attempts (${this.refreshAttempts}) in the last minute`);
      return false;
    }
    
    return true;
  }

  /**
   * Record a refresh attempt
   */
  private recordRefreshAttempt(): void {
    const now = Date.now();
    this.refreshAttempts++;
    this.lastRefreshAttempt = now;
    logger.log(`TokenService: Recorded refresh attempt #${this.refreshAttempts}`);
  }

  /**
   * Record a refresh failure and potentially block further attempts
   */
  private recordRefreshFailure(): void {
    this.refreshFailureCount++;
    logger.log(`TokenService: Recorded refresh failure #${this.refreshFailureCount}`);
    
    if (this.refreshFailureCount >= this.MAX_CONSECUTIVE_FAILURES) {
      this.isRefreshBlocked = true;
      this.refreshBlockedUntil = Date.now() + this.REFRESH_BLOCK_DURATION;
      logger.log(`TokenService: Too many consecutive failures, blocking refresh until ${new Date(this.refreshBlockedUntil).toISOString()}`);
    }
  }

  /**
   * Record a successful refresh
   */
  private recordRefreshSuccess(): void {
    this.refreshFailureCount = 0;
    this.refreshAttempts = 0;
    this.isRefreshBlocked = false;
    this.refreshBlockedUntil = 0;
    this.lastSuccessfulRefresh = Date.now();
    logger.log('TokenService: Refresh success recorded, reset failure count and attempts');
  }

  /**
   * Check if the token is expired or will expire soon
   */
  isTokenExpired(): boolean {
    const tokenInfo = this.getTokenInfo();
    if (!tokenInfo) {
      logger.log('TokenService: No token info found, considering expired');
      return true;
    }

    const now = Date.now();
    const expiresAt = tokenInfo.expiresAt;
    
    logger.log(`TokenService: Token expires at ${new Date(expiresAt).toISOString()}, current time: ${new Date(now).toISOString()}`);
    logger.log(`TokenService: Time until expiry: ${(expiresAt - now) / 1000} seconds`);
    
    // Consider token expired if it expires within the refresh threshold
    const isExpired = now >= (expiresAt - this.REFRESH_THRESHOLD);
    logger.log(`TokenService: Token expired? ${isExpired} (threshold: ${this.REFRESH_THRESHOLD / 1000} seconds)`);
    
    if (isExpired && this.isWithinPostRefreshGracePeriod()) {
      const elapsedSinceRefresh = Date.now() - (this.lastSuccessfulRefresh || 0);
      logger.log(`TokenService: Within post-refresh grace period (${elapsedSinceRefresh}ms), treating token as temporarily valid`);
      return false;
    }

    return isExpired;
  }

  /**
   * Get token information including expiration
   */
  getTokenInfo(): TokenInfo | null {
    try {
      const tokenInfoStr = localStorage.getItem(this.TOKEN_INFO_KEY);
      if (!tokenInfoStr) return null;
      
      const tokenInfo: TokenInfo = JSON.parse(tokenInfoStr);
      
      // Validate the token info structure
      if (!tokenInfo.token || !tokenInfo.expiresAt || !tokenInfo.userId) {
        this.removeToken();
        return null;
      }
      
      return tokenInfo;
    } catch (error) {
      logger.error('Error parsing token info:', error);
      this.removeToken();
      return null;
    }
  }

  /**
   * Update token information in localStorage
   */
  private updateTokenInfo(token: string): void {
    try {
      logger.log('TokenService: Updating token info...');
      const payload = this.decodeToken(token);
      if (!payload) {
        logger.log('TokenService: Failed to decode token, removing token');
        this.removeToken();
        return;
      }

      logger.log('TokenService: Token payload:', payload);
      logger.log('TokenService: Token exp:', payload.exp);

      const tokenInfo: TokenInfo = {
        token,
        expiresAt: payload.exp * 1000, // Convert to milliseconds
        userId: payload.sub
      };

      logger.log('TokenService: Storing token info:', tokenInfo);
      localStorage.setItem(this.TOKEN_INFO_KEY, JSON.stringify(tokenInfo));
      logger.log('TokenService: Token info stored successfully');
      this.emitTokenInfoUpdated(tokenInfo);
    } catch (error) {
      logger.error('TokenService: Error updating token info:', error);
      this.removeToken();
    }
  }

  private emitTokenInfoUpdated(tokenInfo: TokenInfo | null): void {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(
        new CustomEvent(TOKEN_INFO_EVENT, {
          detail: tokenInfo,
        })
      );
    }
  }

  /**
   * Decode JWT token without verification (for client-side use only)
   */
  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      logger.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Refresh the token using the refresh endpoint
   */
  async refreshToken(): Promise<string> {
    // Check if we can attempt a refresh
    if (!this.canAttemptRefresh()) {
      const error = new Error('Refresh rate limited or blocked');
      logger.log('TokenService: Refresh attempt blocked by rate limiting');
      throw error;
    }

    // If there's already a refresh in progress, return the same promise
    if (this.refreshPromise) {
      logger.log('TokenService: Refresh already in progress, returning existing promise');
      return this.refreshPromise;
    }

    // Record the attempt
    this.recordRefreshAttempt();

    this.refreshPromise = this.performRefresh();
    
    try {
      const newToken = await this.refreshPromise;
      this.recordRefreshSuccess();
      return newToken;
    } catch (error) {
      this.recordRefreshFailure();
      throw error;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh
   */
  private async performRefresh(): Promise<string> {
    try {
      const currentToken = this.getToken();
      if (!currentToken) {
        throw new Error('No token available for refresh');
      }

      logger.log('Refreshing token...');
      
      const response = await api.post<RefreshResponse>('/api/v1/auth/refresh', {}, {
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });

      const { access_token } = response.data;
      
      if (!access_token) {
        throw new Error('No access token received from refresh endpoint');
      }

      // Update the token with skipAutoRefresh to prevent recursive calls
      this.setToken(access_token, true);
      
      // Update the default authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      logger.log('Token refreshed successfully');
      
      // Set up auto-refresh for the new token (after successful refresh)
      setTimeout(() => {
        this.setupAutoRefresh();
      }, 100); // Small delay to prevent immediate recursive calls
      
      return access_token;
      
    } catch (error: any) {
      const status = error?.response?.status;
      const isAuthFailure = status === 401 || status === 422;

      if (isAuthFailure) {
        logger.error('Token refresh failed with authentication error:', error);
        this.removeToken();
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login';
        throw new Error('Token refresh failed. Please log in again.');
      }

      logger.error('Token refresh failed due to network/server issue. Will retry automatically.', error);
      setTimeout(() => {
        this.setupAutoRefresh();
      }, this.MIN_REFRESH_INTERVAL);
      throw new Error('Token refresh temporarily unavailable. Please retry.');
    }
  }

  /**
   * Get a valid token, refreshing if necessary
   */
  async getValidToken(): Promise<string> {
    const token = this.getToken();
    
    if (!token) {
      throw new Error('No token available');
    }

    // Check if token is expired or will expire soon
    if (this.isTokenExpired()) {
      logger.log('TokenService: Token expired or expiring soon, attempting refresh...');
      
      // Check rate limiting before attempting refresh
      if (!this.canAttemptRefresh()) {
        logger.log('TokenService: Refresh rate limited, using existing token');
        // Return existing token even if expired - API will handle 401
        return token;
      }
      
      try {
        return await this.refreshToken();
      } catch (error) {
        logger.warn('TokenService: Refresh failed in getValidToken, using existing token:', error);
        // Return existing token as fallback - API will handle 401
        return token;
      }
    }

    return token;
  }

  /**
   * Initialize token service and set up automatic refresh
   */
  initialize(): void {
    const token = this.getToken();
    if (token) {
      // Set the default authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Check if token needs immediate refresh (with rate limiting)
      if (this.isTokenExpired()) {
        logger.log('TokenService: Token needs immediate refresh during initialization');
        
        // Only attempt refresh if not rate limited
        if (this.canAttemptRefresh()) {
          this.refreshToken().catch(error => {
            logger.error('TokenService: Initial token refresh failed:', error);
          });
        } else {
          logger.log('TokenService: Initial refresh skipped due to rate limiting');
          // Set up auto-refresh anyway to retry later
          this.setupAutoRefresh();
        }
      } else {
        // Token is still valid, set up normal auto-refresh
        this.setupAutoRefresh();
      }
    }
  }

  /**
   * Clear any existing auto-refresh timer
   */
  private clearAutoRefresh(): void {
    if (this.autoRefreshTimer) {
      clearTimeout(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
      logger.log('TokenService: Cleared auto-refresh timer');
    }
  }

  pauseAutoRefresh(): void {
    this.clearAutoRefresh();
  }

  resumeAutoRefresh(): void {
    this.setupAutoRefresh();
  }

  /**
   * Set up automatic token refresh before expiry
   */
  setupAutoRefresh(): void {
    // Clear any existing timer first
    this.clearAutoRefresh();
    
    const tokenInfo = this.getTokenInfo();
    if (!tokenInfo) {
      logger.log('TokenService: No token info, skipping auto-refresh setup');
      return;
    }

    // Check if refresh is currently blocked
    if (this.isRefreshCurrentlyBlocked()) {
      logger.log('TokenService: Auto-refresh setup skipped - refresh is currently blocked');
      return;
    }

    const now = Date.now();
    const timeUntilRefresh = tokenInfo.expiresAt - now - this.REFRESH_THRESHOLD;
    
    if (timeUntilRefresh > 0) {
      const scheduleDelay = Math.max(timeUntilRefresh, this.MIN_REFRESH_INTERVAL);
      logger.log(`TokenService: Setting up auto-refresh in ${Math.round(scheduleDelay / 1000)} seconds`);
      
      this.autoRefreshTimer = setTimeout(() => {
        logger.log('TokenService: Auto-refresh timer triggered');
        this.refreshToken()
          .then(() => {
            logger.log('TokenService: Auto-refresh completed successfully');
            // Note: setupAutoRefresh is called from performRefresh after successful refresh
          })
          .catch(error => {
            logger.error('TokenService: Auto-refresh failed:', error);
            // Retry with exponential backoff on failure
            const retryDelay = Math.min(30000, 5000 * Math.pow(2, this.refreshFailureCount));
            logger.log(`TokenService: Retrying auto-refresh in ${retryDelay / 1000} seconds`);
            setTimeout(() => {
              this.setupAutoRefresh();
            }, retryDelay);
          });
      }, scheduleDelay);
    } else {
      // Token needs immediate refresh, but with rate limiting
      logger.log('TokenService: Token needs immediate refresh');

      if (this.isWithinPostRefreshGracePeriod()) {
        const delay = this.getPostRefreshDelay();
        logger.log(`TokenService: Recently refreshed, scheduling next refresh check in ${Math.round(delay / 1000)} seconds to avoid rapid retries`);
        this.autoRefreshTimer = setTimeout(() => {
          this.setupAutoRefresh();
        }, delay);
        return;
      }
      
      // Check if we can attempt immediate refresh
      if (!this.canAttemptRefresh()) {
        logger.log('TokenService: Immediate refresh blocked by rate limiting, scheduling retry');
        // Schedule a retry after the minimum interval
        setTimeout(() => {
          this.setupAutoRefresh();
        }, this.MIN_REFRESH_INTERVAL);
        return;
      }
      
      this.refreshToken()
        .then(() => {
          logger.log('TokenService: Immediate refresh completed successfully');
          // Note: setupAutoRefresh is called from performRefresh after successful refresh
        })
        .catch(error => {
          logger.error('TokenService: Immediate refresh failed:', error);
          // On failure, retry with exponential backoff
          const retryDelay = Math.min(30000, 5000 * Math.pow(2, this.refreshFailureCount));
          logger.log(`TokenService: Retrying immediate refresh in ${retryDelay / 1000} seconds`);
          setTimeout(() => {
            this.setupAutoRefresh();
          }, retryDelay);
        });
    }
  }
}

// Export singleton instance
export const tokenService = new TokenService(); 
